/**
 * Bundle 70 Task 4 - unified user activity timeline card.
 *
 * Renders `GET /api/admin/users/{id}/timeline` output with sticky day
 * headers, per-source filter chips, and inline expand-for-detail. Deep
 * links via `?tab=timeline` and `?filter=nudges` (comma-separated).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Activity, ChevronDown, ChevronRight, RefreshCw,
  Mail, DollarSign, Bell, LogIn, Image as ImageIcon,
  TrendingUp, Ticket, Radio, Download,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const SOURCE_META = {
  pixels: { label: 'Pixels', icon: Radio,
             chip: 'bg-secondary-50 text-secondary-800 border-secondary-200' },
  emails: { label: 'Emails', icon: Mail,
             chip: 'bg-primary-50 text-primary-800 border-primary-200' },
  registrations: { label: 'RSVPs', icon: Ticket,
                    chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  payments: { label: 'Payments', icon: DollarSign,
               chip: 'bg-amber-50 text-amber-800 border-amber-200' },
  nudges: { label: 'Nudges', icon: Bell,
             chip: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  sessions: { label: 'Sessions', icon: LogIn,
               chip: 'bg-neutral-100 text-neutral-800 border-neutral-200' },
  media: { label: 'Media', icon: ImageIcon,
            chip: 'bg-teal-50 text-teal-800 border-teal-200' },
  points: { label: 'Points', icon: TrendingUp,
             chip: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200' },
};

const SEVERITY_STYLE = {
  info: 'text-neutral-500',
  success: 'text-emerald-700',
  warn: 'text-amber-700',
  error: 'text-red-700',
};

const _fmtAbs = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

const _fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.round((now - then) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch { return ''; }
};

const _dayKey = (iso) => (iso || '').slice(0, 10);

const _parseUrlFilter = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const raw = (p.get('filter') || '').trim();
    if (!raw) return null;
    return new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
  } catch { return null; }
};

const TimelineEntry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const meta = SOURCE_META[entry.source] || SOURCE_META.sessions;
  const Icon = meta.icon;
  const sevCls = SEVERITY_STYLE[entry.severity] || SEVERITY_STYLE.info;
  const hasDetail = entry.detail_expanded
    && Object.keys(entry.detail_expanded || {}).length > 0;
  return (
    <li
      className="border border-neutral-100 rounded-md p-2.5 hover:bg-neutral-50 transition-colors"
      data-testid={`timeline-entry-${entry.source}-${entry.ref_id || entry.type}`}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 mt-0.5 ${sevCls}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div className="text-sm font-medium text-primary-900 truncate">
              {entry.label}
            </div>
            <div
              className="text-[11px] text-neutral-500 shrink-0"
              title={_fmtAbs(entry.ts)}
              data-testid={`timeline-entry-ts-${entry.source}`}
            >
              {_fmtRel(entry.ts)}
            </div>
          </div>
          {entry.detail_short ? (
            <div className="text-xs text-neutral-600 mt-0.5 truncate">
              {entry.detail_short}
            </div>
          ) : null}
          {hasDetail ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-secondary-700 hover:text-secondary-900"
              data-testid={`timeline-entry-toggle-${entry.source}`}
            >
              {open ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {open ? 'Hide' : 'Show'} details
            </button>
          ) : null}
          {open && hasDetail ? (
            <pre
              className="mt-1.5 text-[10px] font-mono bg-neutral-100 rounded p-2 whitespace-pre-wrap break-all text-neutral-800"
              data-testid={`timeline-entry-detail-${entry.source}`}
            >
              {JSON.stringify(entry.detail_expanded, null, 2)}
            </pre>
          ) : null}
        </div>
      </div>
    </li>
  );
};

const TimelineCard = ({ userId }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(() => _parseUrlFilter());

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const { data } = await api.get(
          `/admin/users/${userId}/timeline`,
          { params: { days, limit: 500 } },
        );
        if (alive) setData(data);
      } catch (e) {
        if (alive) {
          setError(e?.response?.data?.detail || 'Failed to load timeline');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, days]);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    if (!active || active.size === 0) return data.entries;
    return data.entries.filter((e) => active.has(e.source));
  }, [data, active]);

  const grouped = useMemo(() => {
    const g = {};
    for (const e of filtered) {
      const k = _dayKey(e.ts);
      if (!g[k]) g[k] = [];
      g[k].push(e);
    }
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const toggleSource = (src) => {
    setActive((prev) => {
      const next = new Set(prev || []);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next.size ? next : null;
    });
  };

  const counts = data?.counts || {};

  return (
    <Card data-testid="admin-user-detail-timeline">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm inline-flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" /> Activity timeline
          <span className="text-[10px] text-neutral-400 font-normal">
            ({data?.total || 0})
          </span>
          <div className="ml-auto flex items-center gap-1">
            {[7, 30, 90].map((d) => (
              <Button
                key={d} type="button" size="sm"
                variant={days === d ? 'default' : 'outline'}
                className="h-6 px-2 text-[11px]"
                onClick={() => setDays(d)}
                data-testid={`timeline-window-${d}d`}
              >
                {d}d
              </Button>
            ))}
            <Button
              type="button" size="sm" variant="outline"
              className="h-6 px-2 text-[11px]"
              onClick={() => setDays((d) => d)}
              data-testid="timeline-refresh"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
            {/* Bundle 71 - CSV export of the current timeline view */}
            <Button
              type="button" size="sm" variant="outline"
              className="h-6 px-2 text-[11px]"
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  params.set('days', String(days));
                  if (active && active.size > 0) {
                    params.set('types', Array.from(active).join(','));
                  }
                  const url = `${api.defaults.baseURL}/admin/users/${userId}/timeline.csv?${params.toString()}`;
                  const token = localStorage.getItem('token');
                  const resp = await fetch(url, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  });
                  if (!resp.ok) {
                    toast.error('CSV export failed');
                    return;
                  }
                  const blob = await resp.blob();
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  const stamp = new Date().toISOString().slice(0, 10);
                  a.download = `Dostination-user-${userId}-timeline-${stamp}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                } catch (e) {
                  toast.error('CSV export failed');
                }
              }}
              data-testid="timeline-export-csv"
              title="Download the current timeline view as CSV"
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="flex flex-wrap gap-1.5 mb-3"
          data-testid="timeline-filter-chips"
        >
          {Object.entries(SOURCE_META).map(([src, m]) => {
            const on = active && active.has(src);
            const has = (counts[src] || 0) > 0;
            const Icon = m.icon;
            return (
              <button
                key={src} type="button"
                onClick={() => toggleSource(src)}
                disabled={!has}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                  on ? m.chip : 'bg-white text-neutral-500 border-neutral-200'
                } ${!has ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-50'}`}
                data-testid={`timeline-filter-${src}`}
              >
                <Icon className="h-3 w-3" />
                {m.label}
                <span className="ml-1 font-normal opacity-70">
                  ({counts[src] || 0})
                </span>
              </button>
            );
          })}
          {active ? (
            <button
              type="button"
              onClick={() => setActive(null)}
              className="text-[10px] text-secondary-700 hover:text-secondary-900 underline"
              data-testid="timeline-filter-clear"
            >
              Clear
            </button>
          ) : null}
        </div>
        {loading ? (
          <div
            className="text-xs text-neutral-500 italic p-3"
            data-testid="timeline-loading"
          >
            Loading timeline...
          </div>
        ) : error ? (
          <div className="text-xs text-red-700 italic p-3">
            {String(error)}
          </div>
        ) : grouped.length === 0 ? (
          <div
            className="text-xs text-neutral-500 italic p-3"
            data-testid="timeline-empty"
          >
            No activity in the selected window.
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map(([day, rows]) => (
              <div key={day} data-testid={`timeline-day-${day}`}>
                <div className="sticky top-0 z-[1] bg-white/95 backdrop-blur-sm text-[10px] uppercase tracking-widest text-neutral-500 font-semibold py-1 border-b border-neutral-100 mb-2">
                  {day}
                  <span className="ml-2 text-neutral-400 font-normal normal-case">
                    {rows.length} event{rows.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {rows.map((e, i) => (
                    <TimelineEntry
                      key={`${e.source}-${e.ref_id || i}-${e.ts}`}
                      entry={e}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimelineCard;
